import { CSSProperties } from 'react';

export interface PhaseTick {
  phase: string;
  label: string;
}

interface PhaseRailProps {
  ticks: PhaseTick[];
  activeIndex: number;
  onJump: (index: number) => void;
  /** When true, renders the rail with opacity 0 (still occupies layout).
   *  Used for the 600ms reveal: parent toggles this off via
   *  `useScrubberRevealAnimation`. */
  hidden?: boolean;
}

/** Bottom phase rail for the Scrubber. Horizontal row of ticks; each tick
 *  is a click target. Active tick gets a 14px red mark + white label.
 *  Inactive ticks: 1px stone line + stone label. Sharp corners; uppercase
 *  Oswald labels. */
export function PhaseRail({ ticks, activeIndex, onJump, hidden = false }: PhaseRailProps) {
  if (ticks.length === 0) return null;

  const containerStyle: CSSProperties = {
    display: 'flex',
    width: '100%',
    padding: '0 var(--vm-s-6)',
    paddingBottom: 'var(--vm-s-3)',
    gap: 0,
    opacity: hidden ? 0 : 1,
    transform: hidden ? 'translateY(24px)' : 'translateY(0)',
    filter: hidden ? 'blur(4px)' : 'blur(0)',
    transition:
      'opacity 600ms cubic-bezier(0.16,1,0.3,1), transform 600ms cubic-bezier(0.16,1,0.3,1), filter 600ms cubic-bezier(0.16,1,0.3,1)',
  };

  return (
    <div role="tablist" aria-label="Restoration phases" style={containerStyle}>
      {ticks.map((tick, i) => {
        const active = i === activeIndex;
        return (
          <button
            key={tick.phase}
            type="button"
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            onClick={() => {
              if (!active) onJump(i);
            }}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--vm-s-1)',
              background: 'transparent',
              border: 'none',
              padding: '12px 4px 0',
              cursor: active ? 'default' : 'pointer',
              color: active ? 'var(--vm-warm-white)' : 'var(--vm-stone)',
              transition: 'color 350ms ease',
            }}
          >
            <span
              data-active-mark={active}
              style={{
                display: 'block',
                width: active ? '14px' : '1px',
                height: active ? '2px' : '1px',
                background: active ? 'var(--vm-red)' : 'var(--vm-stone)',
                transition: 'width 250ms ease, height 250ms ease, background 250ms ease',
              }}
            />
            <span
              style={{
                fontFamily: 'var(--vm-font-heading)',
                fontSize: 10,
                letterSpacing: '0.3em',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
              }}
            >
              {tick.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
